/**
 * accountWorkspacesPresentation — pure-logic lift of
 * `AccountWorkspacesSection.tsx`'s Bucket E Workspaces accordion constants
 * (bead terra-design-v2-drilldowns-ppn.6, plan 005 U9). React-free by
 * design (coding standards §1) — `AccountWorkspacesSection.tsx` imports
 * every one of these back and its JSX is otherwise untouched; that file's
 * existing (unmodified) 12-test suite is the parity gate for zero V1
 * behaviour change.
 */
import type { AccountWorkspace, AccountWorkspacesResponse } from '@shared/flow/account-workspaces';
import { money } from './accountFieldFormat';

/** `app.clay.com/workspaces/{id}` link target, or `null` when the row has
 * no resolvable Clay workspace id. */
export function wsAppUrl(workspaceId: string | null): string | null {
  return workspaceId ? `https://app.clay.com/workspaces/${workspaceId}` : null;
}

/** Strip the scheme for a plain-text URL display. */
export function displayHost(url: string | null): string | null {
  return url ? url.replace(/^https?:\/\//, '') : null;
}

export function yesNo(v: boolean): string {
  return v ? 'Yes' : 'No';
}

/** Locale-formatted date matching `cells.tsx`'s `fmtDate` (same `new
 * Date(iso).toLocaleDateString` shape) — duplicated here (not imported)
 * because `fmtDate` returns a React node (`<EmDash/>` on null) and this
 * module stays React-free (coding standards §1). */
function fmtWsDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** ChatGPT Connection cell text — "Yes · <date>" (date only when known) or
 * "No". `hasChatGpt` is a SF checkbox (never null); `chatGptConnectionDate`
 * is only ever set when `hasChatGpt` is true. */
export function chatGptText(hasChatGpt: boolean, chatGptConnectionDate: string | null): string {
  if (!hasChatGpt) return 'No';
  const date = fmtWsDate(chatGptConnectionDate);
  return date ? `Yes · ${date}` : 'Yes';
}

/** Salesforce-verbatim column labels, prod order (prototype:958-959). */
export const WS_COLS = [
  'Workspace Name',
  'Workspace URL',
  'Product Tier',
  'Last Trial Start Date',
  'Last Trial End Date',
  'Active Trial',
  'Active Status',
  'Workspace Creator',
  'ChatGPT Connection',
];

/** V2's Workspaces tab appends ARR/Users after prod's nine — additive,
 * never reorders (coding standards §"Compose With"). */
export const WS_COLS_V2 = [...WS_COLS, 'ARR', 'Users'];

export const WORKSPACE_COPY = {
  loading: 'Loading workspaces…',
  error: 'Workspaces failed to load.',
  noAccess: 'No workspaces access for this account.',
  none: 'None.',
};

/** Collapsed-trigger counts label (`AccountWorkspacesSection.tsx:125-127`)
 * — "(N paid · M trial · K other)", the "· K other" segment omitted
 * entirely when there are no omitted rows (matches
 * `AccountWorkspacesSection.test.tsx`'s "renders no 'other' text ... when
 * omittedCount is zero" contract). */
export function workspaceCountsLabel(data: Pick<AccountWorkspacesResponse, 'paid' | 'trial' | 'omittedCount'>): string {
  const omitted = data.omittedCount ?? 0;
  return `(${data.paid.length} paid · ${data.trial.length} trial${omitted > 0 ? ` · ${omitted} other` : ''})`;
}

/** Paid-table rollup — "{compact $ARR} ARR · {N} users", summing
 * `subscriptionArr`/`numberOfClayUsers` across the paid rows (nullable
 * values contribute zero, never inflate or crash the sum). */
export function paidRollup(rows: AccountWorkspace[]): string {
  const arr = rows.reduce((sum, r) => sum + (r.subscriptionArr ?? 0), 0);
  const users = rows.reduce((sum, r) => sum + (r.numberOfClayUsers ?? 0), 0);
  return `${money(arr)} ARR · ${users} users`;
}

/** Trial-table rollup — "{N} active", counting only rows whose
 * `activeTrial` is true (an inactive trial must not inflate the count). */
export function trialRollup(rows: AccountWorkspace[]): string {
  return `${rows.filter(r => r.activeTrial).length} active`;
}
